import { useCallback, useRef } from 'react'

import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import { useToast } from '@chakra-ui/react'

import { trackEvent } from '@/utils/analytics'

import { FLOOR_PLAN_CHECKPOINTS_ANALYTICS } from '../../constants/constants'
import { CheckpointIProps } from '../../types/types'
import {
  PRINT_CONFIG,
  QR_CODES_PER_PAGE,
  QR_SIZE_RATIO,
  buildPrintDocument,
} from '../utils/bulkPrint'

/**
 * The print window needs a beat after the cloned markup is written before
 * focus/print land reliably; carried over from the legacy modal.
 */
const PRINT_WINDOW_READY_DELAY_MS = 250

interface UsePrintCheckpointQrCodesIProps {
  checkpoints: CheckpointIProps[]
  facilityId: string
  onClose: () => void
}

interface UsePrintCheckpointQrCodesResultIProps {
  /** Attach to the off-screen node holding the print-ready QR canvases. */
  printRef: React.RefObject<HTMLDivElement>
  print: () => void
  totalPages: number
}

/**
 * Owns the print side of the bulk QR flow: it clones the off-screen canvases
 * into a new window as PNGs and triggers the browser print dialog. Shared by
 * BulkPrintModal and its dark twin so the print layout lives in one place.
 */
export const usePrintCheckpointQrCodes = ({
  checkpoints,
  facilityId,
  onClose,
}: UsePrintCheckpointQrCodesIProps): UsePrintCheckpointQrCodesResultIProps => {
  const { t } = useTranslation()
  const printRef = useRef<HTMLDivElement>(null)
  const toast = useToast()

  const totalPages = Math.ceil(checkpoints.length / QR_CODES_PER_PAGE)

  const print = useCallback(() => {
    if (!printRef.current) return

    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600')
    if (!printWindow) {
      Sentry.captureException(new Error('Popup blocked for QR code printing'), {
        tags: {
          action: 'bulk_print',
          feature: 'floor_plan_checkpoints',
          surface: 'bulk_print_modal',
        },
        extra: { checkpointCount: checkpoints.length, facilityId },
      })
      toast({
        title: t('floors:print_popup_blocked_title'),
        description: t('floors:print_popup_blocked_description'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    trackEvent(FLOOR_PLAN_CHECKPOINTS_ANALYTICS.bulkPrintQrCodes, {
      facility_id: facilityId,
      checkpoint_count: checkpoints.length,
      page_count: totalPages,
    })

    // Clone the content and convert canvas to images
    const clonedContent = printRef.current.cloneNode(true) as HTMLElement

    // Find all canvas elements and convert them to images
    const canvases = printRef.current.querySelectorAll('canvas')
    const clonedCanvases = clonedContent.querySelectorAll('canvas')
    let failedCount = 0

    canvases.forEach((canvas, index) => {
      try {
        const dataURL = canvas.toDataURL('image/png')
        const img = document.createElement('img')
        img.src = dataURL
        // Set explicit dimensions - QR is smaller than cell to fit label
        img.style.width = `${PRINT_CONFIG.print.qrSize * QR_SIZE_RATIO}px`
        img.style.height = `${PRINT_CONFIG.print.qrSize * QR_SIZE_RATIO}px`
        img.style.display = 'block'
        img.style.margin = '0 auto'
        clonedCanvases[index].parentNode?.replaceChild(
          img,
          clonedCanvases[index]
        )
      } catch {
        // Canvas might be tainted due to cross-origin restrictions
        failedCount++
      }
    })

    if (failedCount > 0) {
      toast({
        title: t('floors:print_partial_render_title'),
        description: t('floors:print_partial_render_description'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      })
    }

    printWindow.document.write(
      buildPrintDocument(
        clonedContent.innerHTML,
        t('floors:print_window_title')
      )
    )
    printWindow.document.close()

    // Trigger print after content loads
    const triggerPrint = () => {
      setTimeout(() => {
        printWindow.focus()
        printWindow.print()
        printWindow.close()
      }, PRINT_WINDOW_READY_DELAY_MS)
    }

    // Handle race condition: load event might fire before handler is attached
    // since our HTML has no external resources
    if (printWindow.document.readyState === 'complete') {
      triggerPrint()
    } else {
      printWindow.addEventListener('load', triggerPrint, { once: true })
    }

    // Close the modal after initiating print
    onClose()
  }, [toast, t, facilityId, checkpoints.length, totalPages, onClose])

  return { print, printRef, totalPages }
}
